// src/pages/StaffCreatePage.jsx
import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { api } from "../Apis/Api";
import Breadcrumb from "../components/Breadcrumb";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import CustomSelect from "../components/common/CustomSelect";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { generateSecurePassword } from "../utils/generatePassword";

const validationSchema = Yup.object().shape({
  staff_name: Yup.string().required("Name is required"),
  staff_email: Yup.string()
    .required("Email is required")
    .email("Invalid email"),
  staff_phone_no: Yup.string()
    .required("Phone number is required")
    .test("is-valid-phone", "Invalid phone number", (value) =>
      isValidPhoneNumber(value || "")
    ),
  client_role_id: Yup.object()
    .shape({
      value: Yup.string().required("Role is required"),
      label: Yup.string(),
    })
    .required("Role is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  confirm_password: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const StaffCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [clientRoles, setClientRoles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { selectedRole } = location.state || {};
  const { navigationLink } = location.state || false;

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      staff_name: "",
      staff_email: "",
      staff_phone_no: "",
      client_role_id: selectedRole || null,
      password: "",
      confirm_password: "",
    },
  });

  useEffect(() => {
    fetchClientRoles();
  }, []);

  const fetchClientRoles = async () => {
    try {
      setIsLoadingRole(true);
      const res = await api.get("/get-all-client-roles?want_pagination=0");
      const roleOptions = res.data?.data?.map((r) => ({
        label: r.role_name,
        value: r.client_role_id,
      }));
      setClientRoles(roleOptions || []);
      handleApiResponse(res.data, "Fetch Client", "GET");
    } catch (err) {
      const error = err.response?.data || err.message;
      handleApiResponse(error, "Failed to Fetch Client", "GET");
    } finally {
      setIsLoadingRole(false);
    }
  };

  const generatePassword = () => {
    const password = generateSecurePassword();
    setValue("password", password);
    setValue("confirm_password", password);
    setShowPassword(true);
    handleCustomResponse("info", "Password generated!");
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const payload = {
        ...data,
        client_role_id: data.client_role_id.value,
      };

      const res = await api.post("/add-staff", payload);
      handleApiResponse(res.data, "Staff added", "POST");

      // Check if navigationLink exists and has a valid link
      if (navigationLink && navigationLink.link) {
        navigate(`/${navigationLink.link}`);
      } else {
        navigate(-1); // Fallback to previous page
      }
    } catch (err) {
      const error = err.response?.data || err.message;
      handleApiResponse(error, "Staff added Failed", "POST");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Add Staff" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label htmlFor="staff_name" className="form-label">
                  Full Name *
                </label>
                <input
                  id="staff_name"
                  type="text"
                  {...register("staff_name")}
                  className="form-control radius-8"
                  placeholder="Enter Full Name"
                />
                {errors.staff_name && (
                  <span className="text-danger-600">
                    {errors.staff_name.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="staff_email" className="form-label">
                  Email *
                </label>
                <input
                  id="staff_email"
                  type="email"
                  {...register("staff_email")}
                  className="form-control radius-8"
                  placeholder="Enter Email Address"
                />
                {errors.staff_email && (
                  <span className="text-danger-600">
                    {errors.staff_email.message}
                  </span>
                )}
              </div>
              <div className="col-md-6 mb-20">
                <CustomPhoneInput
                  name="staff_phone_no"
                  control={control}
                  errors={errors}
                />
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Role *</label>
                <Controller
                  name="client_role_id"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="client_role_id"
                      value={field.value}
                      onChange={field.onChange}
                      options={clientRoles}
                      placeholder="Select Role"
                      isClearable
                      isLoading={isLoadingRole}
                      noOptionsMessage={() => (
                        <div>
                          <p>No roles available</p>
                        </div>
                      )}
                      addNewLabel="Add New Role"
                      addNewRoute="/staff-role-create"
                    />
                  )}
                />
                {errors.client_role_id && (
                  <span className="text-danger-600">
                    {errors.client_role_id.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Password *</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className="form-control radius-8"
                    autoComplete="new-password"
                    style={{ paddingRight: "4rem" }}
                    placeholder="Enter Password"
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "2rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={generatePassword}
                  >
                    <Icon icon="carbon:password" />
                  </span>
                  <span
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon
                      icon={showPassword ? "ri:eye-off-line" : "ri:eye-line"}
                    />
                  </span>
                </div>
                {errors.password && (
                  <span className="text-danger-600">
                    {errors.password.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Confirm Password *</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirm_password")}
                    className="form-control radius-8"
                    autoComplete="new-password"
                    style={{ paddingRight: "2.5rem" }}
                    placeholder="Confirm Password"
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Icon
                      icon={
                        showConfirmPassword ? "ri:eye-off-line" : "ri:eye-line"
                      }
                    />
                  </span>
                </div>
                {errors.confirm_password && (
                  <span className="text-danger-600">
                    {errors.confirm_password.message}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={isLoading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default StaffCreatePage;
